import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import TrimbleAddressAutocomplete from '@/components/TrimbleAddressAutocomplete';
import logoLight from '@/assets/logo-horizontal-light.png';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID',
  'IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO',
  'MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA',
  'RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

// Blocks the whole app behind a mandatory "who are you" form for any
// staff member whose staff_profiles row has no full_name yet -- e.g.
// every invite accepted through accept_invitation's staff_profiles
// mirror (0012), which never asked for or set one. Without this a
// staff member could use the entire app -- including showing up in
// Team Chat -- identified only by their raw email address forever,
// since nothing else ever prompts for it.
//
// first_name/last_name are stored separately (for real name-field
// use elsewhere later) but ALSO combined into full_name on save,
// since that's the single field every existing display (Team Chat,
// Staff Members table, etc.) already reads.
export default function CompleteStaffProfileGate({ profile, onComplete }) {
  // Legal/HR paperwork needs to read clearly for a brand-new user who has
  // no context on the app's theme toggle yet -- force light regardless of
  // their (possibly just-defaulted-to-dark) theme preference, same
  // treatment CarrierAuth/AcceptInvite/LegalShell already get. This is a
  // full-screen gate (nothing else renders alongside it), so toggling the
  // root .dark class for its lifetime is safe.
  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains('dark');
    root.classList.remove('dark');
    return () => { if (wasDark) root.classList.add('dark'); };
  }, []);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) { toast.error('First and last name are required.'); return; }
    setSaving(true);
    try {
      await client.entities.StaffProfile.update(profile.id, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        full_name: `${firstName.trim()} ${lastName.trim()}`,
        phone: phone.trim(),
        address: address.trim(),
        address2: address2.trim(),
        city: city.trim(),
        state,
        zip: zip.trim(),
      });
      toast.success('Profile complete.');
      onComplete();
    } catch (e) {
      toast.error(e?.message || 'Could not save your profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <img src={logoLight} alt="Pro Haul" className="w-56 mx-auto mb-6" />
          <h1 className="font-space font-bold text-xl text-foreground">Complete your profile</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Your name is shown to teammates in Team Chat. Everything else here stays in your HR record.
          </p>
        </div>
        <form onSubmit={submit} className="glass-card p-5 space-y-4">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Email</label>
            <Input value={profile.user_email || ''} disabled className="text-foreground opacity-70" />
            <p className="text-xs text-muted-foreground mt-1">From your invite -- can't be changed here.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">First Name *</label>
              <Input value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Jane" autoFocus className="text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Last Name *</label>
              <Input value={lastName} onChange={e => setLastName(e.target.value)} placeholder="Smith" className="text-foreground" />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Phone Number</label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 (555) 000-0000" className="text-foreground" />
          </div>
          <div>
            <TrimbleAddressAutocomplete
              value={address}
              onChange={e => setAddress(e.target.value)}
              onSelect={data => {
                setAddress(data.street || address);
                if (data.city) setCity(data.city);
                if (data.state) setState(data.state);
                if (data.zip) setZip(data.zip);
              }}
              label="Address"
              placeholder="123 Main St"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Address 2</label>
            <Input value={address2} onChange={e => setAddress2(e.target.value)} placeholder="Apt, suite, unit (optional)" className="text-foreground" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1">
              <label className="text-xs text-muted-foreground block mb-1">City</label>
              <Input value={city} onChange={e => setCity(e.target.value)} placeholder="Las Vegas" className="text-foreground" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">State</label>
              <select
                value={state}
                onChange={e => setState(e.target.value)}
                className="w-full h-10 px-3 rounded-lg bg-transparent border border-input text-foreground text-sm focus:outline-none"
              >
                <option value="" className="bg-background">—</option>
                {US_STATES.map(s => <option key={s} value={s} className="bg-background">{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">Zip</label>
              <Input value={zip} onChange={e => setZip(e.target.value)} placeholder="89110" className="text-foreground" />
            </div>
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-prohaul-black text-prohaul-white hover:bg-prohaul-black/90">
            {saving ? 'Saving…' : 'Continue'}
          </Button>
        </form>
      </div>
    </div>
  );
}
